import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { Plus } from "lucide-react"
import { useState } from "react"
import { useSearchParams } from "react-router"
import { MasterDataEntityPage } from "../MasterDataEntityPage"
import { MasterDataPanelLayout } from "../MasterDataPanelLayout"
import { CreateSubstanceModal } from "../substances/CreateSubstanceModal"
import { SubstanceDetailPanel } from "../substances/SubstanceDetailPanel"
import { SubstanceListPanel } from "../substances/SubstanceListPanel"

export function SubstancesPage() {
  const { translate } = useTranslation()
  const canEditSubstances = useFeatureFlag("MC_MASTER_DATA_SUBSTANCE_EDIT")
  const { hasPermission } = useUser()
  // Same gate as the header button: the empty-state action must not offer a
  // create that the backend refuses (flag off) or the user may not do.
  const canAddSubstance =
    canEditSubstances && hasPermission(PERMISSIONS.MC_MASTER_DATA_EDIT)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [, setSearchParams] = useSearchParams()

  // Success and "use existing substance" (409 conflict) both end with a real
  // substance id to act on — select it in the list/detail panel the same way
  // a row click does, so either path lands the user on what they were after.
  // The substance is very likely NOT yet linked to any article in this org
  // (that's exactly why "use existing" fired, and a brand new substance has
  // no links either) — the org-scoped detail read 404s without a link, so
  // this must land in catalog view, whose detail read has no such
  // requirement, or the panel silently shows nothing.
  // `source=nav` reuses the existing cross-entity-navigation convention
  // (SubstanceListPanel's useNavSeedSearch): it seeds the list's own search
  // box with this substance's name/CAS, narrowing the left-hand list to just
  // the one row now shown on the right — otherwise it's a whole 304-row
  // catalog on the left next to one selected row on the right.
  function handleSubstanceCreated(substanceId: string) {
    setIsCreateOpen(false)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", substanceId)
      next.set("view", "catalog")
      next.set("source", "nav")
      return next
    })
  }

  return (
    <MasterDataEntityPage
      titleKey="MC_MD_TAB_SUBSTANCES"
      helpArticleId="master-data.articles"
      headerActions={
        canEditSubstances ? (
          <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {translate("MC_MD_PANEL_SUBSTANCES_ADD")}
            </Button>
          </PermissionGate>
        ) : undefined
      }
    >
      <MasterDataPanelLayout
        listPanel={
          <SubstanceListPanel
            onAddSubstance={
              canAddSubstance ? () => setIsCreateOpen(true) : undefined
            }
          />
        }
        detailPanel={<SubstanceDetailPanel />}
      />
      <CreateSubstanceModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={handleSubstanceCreated}
      />
    </MasterDataEntityPage>
  )
}
